import {
    MAX_VALIDATION_DOCUMENTS_PER_TYPE,
    VALIDATION_DOCUMENT_MAX_BYTES,
    VALIDATION_DOCUMENT_MAX_SIZE_MB,
    VALIDATION_DOCUMENT_MIME_TYPES,
    ValidationDocumentType,
} from '@houzy/schemas/partner-management/partner-validation';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useId, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { isCompanyUidFormatInvalid, uidDigits } from '../../../components/companyUid';
import { CompanyUidField } from '../../../components/CompanyUidField';
import { DocumentUploader } from '../../../components/DocumentUploader';
import { useTrackEvent } from '../../../lib/trackingEvents';
import { useDirtyForm } from '../../settings/state/useDirtyForm';
import {
    useAddValidationDocumentMutation,
    useDeleteValidationDocumentMutation,
    useSaveValidationMutation,
} from '../api/validationApi';
import {
    canEditPartnerCockpitValidation,
    canSubmitPartnerCockpitValidation,
    showsIdentityDocuments,
    type PartnerCockpitValidation,
} from '../types';
import { SubmitValidationDialog } from './SubmitValidationDialog';

export type VerificationCardProps = {
    validation: PartnerCockpitValidation;
};

export function VerificationCard({ validation }: VerificationCardProps): ReactElement {
    const { t } = useTranslation();
    const headingId = useId();

    const { value: uid, setValue: setUid, reset, isDirty: uidIsDirty } = useDirtyForm(uidDigits(validation.uid));
    // Documents are already stored by the time they appear, so there is no value to diff — only the fact that the
    // craftsman touched them, which is what makes submitting worthwhile.
    const [documentsTouched, setDocumentsTouched] = useState(false);
    const [confirmingSubmit, setConfirmingSubmit] = useState(false);
    const save = useSaveValidationMutation();
    const track = useTrackEvent();
    const addDocument = useAddValidationDocumentMutation();
    const deleteDocument = useDeleteValidationDocumentMutation();

    const state = validation.state;
    const editable = canEditPartnerCockpitValidation(state);

    const trimmedUid = uid.trim();
    const uidFormatInvalid = isCompanyUidFormatInvalid(uid);

    const isDirty = uidIsDirty || documentsTouched;

    // Not gated on the form being dirty: a craftsman who provided everything during onboarding lands here with nothing
    // to change and still has to be able to hand it over.
    const canSave =
        !save.isPending &&
        !uidFormatInvalid &&
        canSubmitPartnerCockpitValidation({
            state,
            hasUid: trimmedUid.length > 0,
            hasDocuments: validation.documents.length > 0,
        });

    const documentsOfType = (
        type: ValidationDocumentType,
    ): ReadonlyArray<PartnerCockpitValidation['documents'][number]> =>
        validation.documents.filter((document) => document.type === type);

    const handleSubmit = (): void => {
        save.mutate(
            { uid: trimmedUid.length > 0 ? trimmedUid : null },
            {
                onSuccess: () => {
                    track('Verification Submitted', {});
                    setDocumentsTouched(false);
                    setConfirmingSubmit(false);
                },
            },
        );
    };

    const recordDocumentChange = <T,>(change: Promise<T>): Promise<void> =>
        change.then(() => {
            setDocumentsTouched(true);
        });

    return (
        <Card variant="outlined" component="section" aria-labelledby={headingId}>
            <CardHeader
                title={t('SETTINGS.VERIFICATION.TITLE')}
                slotProps={{ title: { variant: 'h6', component: 'h2', id: headingId } }}
            />
            <CardContent sx={{ pt: 0 }}>
                <Stack spacing={3}>
                    <CompanyUidField
                        label={t('SETTINGS.VERIFICATION.UID_LABEL')}
                        value={uid}
                        onChange={setUid}
                        disabled={!editable}
                    />

                    <DocumentUploader
                        label={t('SETTINGS.VERIFICATION.DOCS.COMPANY_INSURANCE')}
                        description={t('SETTINGS.VERIFICATION.DOCS.COMPANY_INSURANCE_DESCRIPTION')}
                        acceptedMimeTypes={VALIDATION_DOCUMENT_MIME_TYPES.CompanyAssuranceDocument}
                        maxSizeBytes={VALIDATION_DOCUMENT_MAX_BYTES}
                        maxCount={MAX_VALIDATION_DOCUMENTS_PER_TYPE.CompanyAssuranceDocument}
                        hint={t('SETTINGS.VERIFICATION.DOCS.HINT', { mb: VALIDATION_DOCUMENT_MAX_SIZE_MB })}
                        documents={documentsOfType(ValidationDocumentType.CompanyAssuranceDocument)}
                        readOnly={!editable}
                        onUpload={(file) =>
                            recordDocumentChange(
                                addDocument.mutateAsync({
                                    type: ValidationDocumentType.CompanyAssuranceDocument,
                                    file,
                                }),
                            )
                        }
                        onRemove={(uuid) => recordDocumentChange(deleteDocument.mutateAsync(uuid))}
                    />

                    {/* Once Partner Management has ruled, the identity documents are purged and there is nothing to show. */}
                    {showsIdentityDocuments(state) && (
                        <Stack spacing={1}>
                            <DocumentUploader
                                label={t('SETTINGS.VERIFICATION.DOCS.ID_CARD')}
                                description={t('SETTINGS.VERIFICATION.DOCS.ID_CARD_DESCRIPTION')}
                                acceptedMimeTypes={VALIDATION_DOCUMENT_MIME_TYPES.IdentityDocument}
                                maxSizeBytes={VALIDATION_DOCUMENT_MAX_BYTES}
                                maxCount={MAX_VALIDATION_DOCUMENTS_PER_TYPE.IdentityDocument}
                                hint={t('SETTINGS.VERIFICATION.DOCS.HINT', { mb: VALIDATION_DOCUMENT_MAX_SIZE_MB })}
                                documents={documentsOfType(ValidationDocumentType.IdentityDocument)}
                                readOnly={!editable}
                                onUpload={(file) =>
                                    recordDocumentChange(
                                        addDocument.mutateAsync({
                                            type: ValidationDocumentType.IdentityDocument,
                                            file,
                                        }),
                                    )
                                }
                                onRemove={(uuid) => recordDocumentChange(deleteDocument.mutateAsync(uuid))}
                            />
                            <Typography variant="caption" color="text.secondary">
                                {t('SETTINGS.VERIFICATION.DOCS.ID_PRIVACY_NOTICE')}
                            </Typography>
                        </Stack>
                    )}

                    {/* Stale as soon as they start changing things again, so it goes when the form turns dirty. */}
                    {save.isSuccess && !isDirty && (
                        <Alert severity="success">{t('SETTINGS.VERIFICATION.SUBMITTED')}</Alert>
                    )}
                </Stack>
            </CardContent>

            {editable && (
                <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setConfirmingSubmit(true)}
                        disabled={!canSave}
                        startIcon={save.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
                        {t('SETTINGS.VERIFICATION.ACTIONS.SUBMIT')}
                    </Button>
                    {/* Only the UID can be put back — a document is already stored by the time it is listed. */}
                    <Button variant="outlined" color="inherit" onClick={reset} disabled={!uidIsDirty || save.isPending}>
                        {t('SETTINGS.VERIFICATION.ACTIONS.CANCEL')}
                    </Button>
                </CardActions>
            )}

            <SubmitValidationDialog
                open={confirmingSubmit}
                isSubmitting={save.isPending}
                onConfirm={handleSubmit}
                onCancel={() => setConfirmingSubmit(false)}
            />
        </Card>
    );
}
